import { useState, useRef } from 'react'
import { useForm } from '@inertiajs/react'
import { useFlashToast } from '../../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPaperPlane, faChevronLeft, faLocationDot, faCalendar,
  faHeading, faEyeSlash, faCircleInfo, faCheck,
  faCloudArrowUp, faPaperclip, faXmark, faTriangleExclamation, faFile,
  faShieldHalved
} from '@fortawesome/free-solid-svg-icons'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import {
  ClassicEditor, Bold, Italic, Underline, Essentials,
  Heading, List, Paragraph, Undo, BlockQuote
} from 'ckeditor5'
import 'ckeditor5/ckeditor5.css'

const CK_STYLE = `
  #ck-uraian .ck-editor__editable_inline {
    min-height: 320px !important;
    font-size: 0.875rem !important;
    line-height: 1.7 !important;
    padding: 12px 16px !important;
  }
`

import UserSidebar from '../../../components/user/Sidebar'
import UserTopbar from '../../../components/user/Topbar'

const CATEGORIES = [
  { label: 'Korupsi & Gratifikasi',    emoji: '🏛️', desc: 'Penyalahgunaan jabatan untuk keuntungan pribadi atau penerimaan gratifikasi'   },
  { label: 'Penyalahgunaan Wewenang',  emoji: '⚖️', desc: 'Penggunaan jabatan di luar batas kewenangan yang ditetapkan'                    },
  { label: 'Konflik Kepentingan',      emoji: '🔄', desc: 'Benturan kepentingan pribadi dengan tugas dan fungsi jabatan'                    },
  { label: 'Penggelapan Aset Negara',  emoji: '📦', desc: 'Penguasaan, penggunaan, atau pengalihan aset negara secara tidak sah'            },
  { label: 'Suap & Pungli',            emoji: '💰', desc: 'Pemberian atau penerimaan suap serta pungutan liar di luar ketentuan'            },
  { label: 'Pemalsuan Dokumen',        emoji: '📄', desc: 'Pembuatan, pengubahan, atau penggunaan dokumen palsu'                            },
  { label: 'Pelanggaran Kode Etik',    emoji: '📋', desc: 'Pelanggaran norma etika dan perilaku dalam lingkungan pemerintahan'              },
  { label: 'Kecurangan Pengadaan',     emoji: '🛒', desc: 'Manipulasi proses pengadaan barang dan jasa pemerintah'                         },
]

const STEPS = ['Kategori', 'Detail Laporan', 'Konfirmasi']

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((step, i) => {
        const done = i < current
        const active = i === current
        return (
          <div key={step} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 ${active ? '' : ''}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                done ? 'bg-green-600 text-white' : active ? 'bg-green-600 text-white ring-4 ring-green-100' : 'bg-gray-100 text-gray-400'
              }`}>
                {done ? <FontAwesomeIcon icon={faCheck} className="w-3 h-3" /> : i + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${active ? 'text-green-700' : done ? 'text-gray-600' : 'text-gray-400'}`}>{step}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`w-8 h-0.5 ${i < current ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function UserReportsNew({ current_user, categories }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [step, setStep] = useState(0)
  const [descError, setDescError] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)

  const form = useForm({
    category: '',
    title: '',
    description: '',
    location: '',
    incident_date: '',
    anonymous: false,
    attachments: [],
  })

  const fileInputRef = useRef(null)
  const [fileErrors, setFileErrors] = useState([])
  const [isDragging, setIsDragging] = useState(false)

  const MAX_FILES = 10
  const MAX_SIZE  = 5 * 1024 * 1024

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleFiles = (incoming) => {
    const errors = []
    const valid = []
    const existing = form.data.attachments
    Array.from(incoming).forEach(file => {
      if (existing.length + valid.length >= MAX_FILES) {
        errors.push(`Maksimal ${MAX_FILES} file — "${file.name}" dilewati.`)
        return
      }
      if (file.size > MAX_SIZE) {
        errors.push(`"${file.name}" melebihi batas 5MB.`)
        return
      }
      if (existing.some(f => f.name === file.name && f.size === file.size)) {
        errors.push(`"${file.name}" sudah ditambahkan.`)
        return
      }
      valid.push(file)
    })
    setFileErrors(errors)
    if (valid.length > 0) form.setData('attachments', [...existing, ...valid])
  }

  const removeFile = (idx) => {
    form.setData('attachments', form.data.attachments.filter((_, i) => i !== idx))
    setFileErrors([])
  }

  const selectedCat = CATEGORIES.find(c => c.label === form.data.category)

  const handleSubmit = (e) => {
    e.preventDefault()
    setShowConfirm(true)
  }

  const confirmAndSubmit = () => {
    form.post('/user/reports', { forceFormData: true })
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <UserSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <UserTopbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <a href="/user/reports" className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-500 transition-colors">
              <FontAwesomeIcon icon={faChevronLeft} className="w-3.5 h-3.5" />
            </a>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Buat Laporan</h1>
              <p className="text-sm text-gray-500 mt-0.5">Laporkan dugaan pelanggaran di lingkungan Pemprov Kalsel</p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto">

            <StepIndicator current={step} />

            {/* Step 0: Pilih Kategori */}
            {step === 0 && (
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="px-6 py-5 border-b border-gray-100">
                  <h2 className="text-base font-semibold text-gray-800">Pilih Kategori Laporan</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Pilih kategori yang paling sesuai dengan laporan Anda</p>
                </div>
                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.label}
                      type="button"
                      onClick={() => form.setData('category', cat.label)}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        form.data.category === cat.label
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-100 hover:border-gray-300 bg-gray-50 hover:bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-1.5">
                        <span className="text-2xl leading-none">{cat.emoji}</span>
                        <span className={`text-sm font-semibold ${form.data.category === cat.label ? 'text-green-800' : 'text-gray-800'}`}>
                          {cat.label}
                        </span>
                        {form.data.category === cat.label && (
                          <div className="ml-auto w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                            <FontAwesomeIcon icon={faCheck} className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">{cat.desc}</p>
                    </button>
                  ))}
                </div>
                <div className="px-6 pb-6 flex justify-end">
                  <button
                    type="button"
                    disabled={!form.data.category}
                    onClick={() => setStep(1)}
                    className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
                  >
                    Lanjutkan
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Detail Laporan */}
            {step === 1 && (
              <form onSubmit={e => {
                e.preventDefault()
                const plain = form.data.description.replace(/<[^>]*>/g, '').trim()
                if (plain.length < 30) { setDescError('Uraian minimal 30 karakter'); return }
                setDescError('')
                setStep(2)
              }}
                className="bg-white rounded-xl border border-gray-200">
                <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
                  <span className="text-2xl">{selectedCat?.emoji}</span>
                  <div>
                    <h2 className="text-base font-semibold text-gray-800">Detail Laporan</h2>
                    <p className="text-xs text-green-700 font-medium mt-0.5">{form.data.category}</p>
                  </div>
                </div>
                <div className="p-6 space-y-5">

                  {/* Judul */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Judul Laporan <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <FontAwesomeIcon icon={faHeading} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        required
                        minLength={10}
                        maxLength={200}
                        value={form.data.title}
                        onChange={e => form.setData('title', e.target.value)}
                        placeholder="Ringkasan singkat laporan Anda (min. 10 karakter)"
                        className="w-full pl-9 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition-all"
                      />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">{form.data.title.length}/200 karakter</p>
                  </div>

                  {/* Deskripsi */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Uraian Pelanggaran <span className="text-red-400">*</span>
                    </label>
                    <style>{CK_STYLE}</style>
                    <div id="ck-uraian" className={`rounded-lg border overflow-hidden transition-all ${
                      descError
                        ? 'border-red-400 ring-2 ring-red-400/20'
                        : 'border-gray-300 focus-within:border-green-500 focus-within:ring-2 focus-within:ring-green-500/20'
                    }`}>
                      <CKEditor
                        editor={ClassicEditor}
                        data={form.data.description}
                        onChange={(_event, editor) => {
                          form.setData('description', editor.getData())
                          if (descError) setDescError('')
                        }}
                        config={{
                          licenseKey: 'GPL',
                          toolbar: {
                            items: ['heading', '|', 'bold', 'italic', 'underline', '|', 'bulletedList', 'numberedList', '|', 'blockQuote', '|', 'undo', 'redo'],
                          },
                          plugins: [Essentials, Bold, Italic, Underline, Heading, List, Paragraph, Undo, BlockQuote],
                          placeholder: 'Jelaskan secara detail dugaan pelanggaran yang Anda ketahui. Semakin lengkap informasinya, semakin mudah diverifikasi.',
                        }}
                      />
                    </div>
                    {descError && <p className="text-[11px] text-red-400 mt-1">{descError}</p>}
                    <p className="text-[11px] text-gray-400 mt-1">Minimal 30 karakter. Gunakan toolbar untuk memformat teks.</p>
                  </div>

                  {/* Lokasi & Tanggal */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Lokasi Kejadian</label>
                      <div className="relative">
                        <FontAwesomeIcon icon={faLocationDot} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="text"
                          value={form.data.location}
                          onChange={e => form.setData('location', e.target.value)}
                          placeholder="Contoh: Dinas Pekerjaan Umum"
                          className="w-full pl-9 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tanggal Kejadian</label>
                      <div className="relative">
                        <FontAwesomeIcon icon={faCalendar} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="date"
                          value={form.data.incident_date}
                          max={new Date().toISOString().split('T')[0]}
                          onChange={e => form.setData('incident_date', e.target.value)}
                          className="w-full pl-9 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bukti Dukung */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Bukti Dukung
                      <span className="ml-1 font-normal text-gray-400">(opsional · maks. {MAX_FILES} file · maks. 5MB/file)</span>
                    </label>

                    {/* Drop zone */}
                    <div
                      onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={e => { e.preventDefault(); setIsDragging(false); handleFiles(e.dataTransfer.files) }}
                      onClick={() => fileInputRef.current?.click()}
                      className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-7 text-center cursor-pointer transition-all ${
                        isDragging ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-400 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={e => { handleFiles(e.target.files); e.target.value = '' }}
                      />
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                        isDragging ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <FontAwesomeIcon icon={faCloudArrowUp} className={`w-5 h-5 ${isDragging ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          {isDragging ? 'Lepaskan file di sini' : 'Klik atau drag file ke sini'}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">PDF, DOC, gambar, ZIP, dll.</p>
                      </div>
                    </div>

                    {/* File list */}
                    {form.data.attachments.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {form.data.attachments.map((file, idx) => (
                          <li key={idx} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100">
                            <div className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FontAwesomeIcon icon={faFile} className="w-3.5 h-3.5 text-gray-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                              <p className="text-[11px] text-gray-400">{formatBytes(file.size)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(idx)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                            >
                              <FontAwesomeIcon icon={faXmark} className="w-3.5 h-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Quota indicator */}
                    {form.data.attachments.length > 0 && (
                      <p className="text-[11px] text-gray-400 mt-2">
                        {form.data.attachments.length}/{MAX_FILES} file dipilih
                      </p>
                    )}

                    {/* Errors */}
                    {fileErrors.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {fileErrors.map((err, i) => (
                          <p key={i} className="text-[11px] text-red-500 flex items-center gap-1.5">
                            <FontAwesomeIcon icon={faTriangleExclamation} className="w-3 h-3 flex-shrink-0" />
                            {err}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Anonymous toggle */}
                  <div className={`rounded-xl border-2 p-4 transition-all ${form.data.anonymous ? 'border-green-300 bg-green-50' : 'border-gray-100 bg-gray-50'}`}>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div
                        onClick={() => form.setData('anonymous', !form.data.anonymous)}
                        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${form.data.anonymous ? 'bg-green-600' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.data.anonymous ? 'translate-x-5' : 'translate-x-0'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faEyeSlash} className="w-3.5 h-3.5 text-gray-500" />
                          <span className="text-sm font-semibold text-gray-800">Kirim sebagai Anonim</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">Identitas Anda tidak akan ditampilkan kepada pihak yang dilaporkan</p>
                      </div>
                    </label>
                  </div>

                </div>

                <div className="px-6 pb-6 flex justify-between">
                  <button type="button" onClick={() => setStep(0)}
                    className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-100 text-sm font-medium rounded-lg transition-colors">
                    <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" /> Kembali
                  </button>
                  <button type="submit"
                    className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors">
                    Tinjau Laporan
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Konfirmasi */}
            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-4">

                {/* Preview card */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="h-1.5 bg-gradient-to-r from-green-500 to-emerald-400" />
                  <div className="px-6 py-5 border-b border-gray-100">
                    <h2 className="text-base font-semibold text-gray-800">Tinjau Laporan Anda</h2>
                    <p className="text-xs text-gray-400 mt-0.5">Periksa kembali sebelum mengirimkan</p>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                      <span className="text-3xl">{selectedCat?.emoji}</span>
                      <div>
                        <p className="text-xs text-green-700 font-semibold">{form.data.category}</p>
                        <p className="text-base font-bold text-gray-900">{form.data.title}</p>
                      </div>
                      {form.data.anonymous && (
                        <span className="ml-auto flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                          <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3" /> Anonim
                        </span>
                      )}
                    </div>

                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Uraian</p>
                      <div
                        className="text-sm text-gray-700 leading-relaxed [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:text-gray-500 [&_h2]:text-base [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-bold"
                        dangerouslySetInnerHTML={{ __html: form.data.description }}
                      />
                    </div>

                    {(form.data.location || form.data.incident_date) && (
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                        {form.data.location && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Lokasi</p>
                            <p className="text-sm text-gray-700">{form.data.location}</p>
                          </div>
                        )}
                        {form.data.incident_date && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Tanggal Kejadian</p>
                            <p className="text-sm text-gray-700">{form.data.incident_date}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {form.data.attachments.length > 0 && (
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                          Bukti Dukung ({form.data.attachments.length} file)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {form.data.attachments.map((file, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-full max-w-[200px]">
                              <FontAwesomeIcon icon={faPaperclip} className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{file.name}</span>
                              <span className="text-gray-400 flex-shrink-0">· {formatBytes(file.size)}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Info box */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
                  <FontAwesomeIcon icon={faCircleInfo} className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-700 leading-relaxed">
                    <p className="font-semibold mb-1">Apa yang terjadi setelah laporan dikirim?</p>
                    <ul className="list-disc list-inside space-y-0.5 text-blue-600">
                      <li>Anda akan mendapat <strong>kode pelacakan unik</strong> untuk memantau status laporan</li>
                      <li>Tim kami akan memverifikasi laporan dalam <strong>3–5 hari kerja</strong></li>
                      <li>Semua laporan diperlakukan <strong>rahasia dan terlindungi</strong></li>
                    </ul>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button type="button" onClick={() => setStep(1)}
                    className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-100 text-sm font-medium rounded-lg transition-colors">
                    <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" /> Kembali
                  </button>
                  <button
                    type="submit"
                    disabled={form.processing}
                    className="flex items-center gap-2 px-7 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm shadow-green-200"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="w-3.5 h-3.5" />
                    {form.processing ? 'Mengirim...' : 'Kirim Laporan'}
                  </button>
                </div>
              </form>
            )}

          </div>
        </main>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !form.processing && setShowConfirm(false)}
          />

          {/* Dialog */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Green accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-green-500 to-emerald-400" />

            <div className="p-6">
              {/* Icon */}
              <div className="flex items-center justify-center w-14 h-14 bg-green-50 rounded-2xl mx-auto mb-4">
                <FontAwesomeIcon icon={faShieldHalved} className="w-7 h-7 text-green-600" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 text-center">Konfirmasi Pengiriman</h3>
              <p className="text-sm text-gray-500 text-center mt-1.5 leading-relaxed">
                Apakah Anda yakin ingin mengirimkan laporan ini? Laporan yang telah dikirim
                tidak dapat diubah.
              </p>

              {/* Summary */}
              <div className="mt-5 bg-gray-50 rounded-xl border border-gray-100 p-4 space-y-2.5">
                <div className="flex items-start gap-2">
                  <span className="text-base leading-none mt-0.5">{selectedCat?.emoji}</span>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Kategori</p>
                    <p className="text-sm font-semibold text-green-700">{form.data.category}</p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Judul</p>
                  <p className="text-sm text-gray-800 font-medium">{form.data.title}</p>
                </div>
                {form.data.attachments.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Bukti Dukung</p>
                    <p className="text-sm text-gray-600">{form.data.attachments.length} file terlampir</p>
                  </div>
                )}
                {form.data.anonymous && (
                  <div className="flex items-center gap-1.5 pt-1.5 border-t border-gray-100">
                    <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">Laporan akan dikirim sebagai anonim</span>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400 text-center mt-3">
                Konfirmasi email akan dikirim ke <span className="font-medium text-gray-600">{current_user?.email}</span>
              </p>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                type="button"
                disabled={form.processing}
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50 text-sm font-medium rounded-xl transition-colors"
              >
                Batalkan
              </button>
              <button
                type="button"
                disabled={form.processing}
                onClick={confirmAndSubmit}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-green-200"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="w-3.5 h-3.5" />
                {form.processing ? 'Mengirim...' : 'Ya, Kirim Sekarang'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
